"use client";

import { useEffect, useRef, useState, useCallback, Fragment } from "react";
import { useParams } from "next/navigation";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { FundSummaryCard } from "@/components/funds/fund-summary-card";
import { FundSection } from "@/components/funds/fund-section";
import { FundScoreBreakdown } from "@/components/funds/fund-score-breakdown";
import { FundHoldingsChart } from "@/components/funds/fund-holdings-chart";
import { FundDisclaimer } from "@/components/funds/fund-disclaimer";
import { FundPaywallModal } from "@/components/funds/fund-paywall-modal";
import { DualTabToggle } from "@/components/shared/dual-tab-toggle";
import { Skeleton } from "@/components/ui/skeleton";
import { FUND_SECTION_META } from "@/lib/types/fund";
import type { FundReportContent, PreCalculatedFundData } from "@/lib/types/fund";
import { stripMarkdown } from "@/lib/utils/format";
import type { GlossaryEntry } from "@/lib/utils/highlight-glossary";
import { FundIndustrySection } from "@/components/industry/FundIndustrySection";
import type { EmbeddedFundIndustryAnalysis } from "@/lib/types/industry";

// Chart + info components
import { FundReturnsChart } from "@/components/funds/fund-returns-chart";
import { FundSectorChart } from "@/components/funds/fund-sector-chart";
import { FundScoreRadar } from "@/components/funds/fund-score-radar";
import { FundDrawdownCard } from "@/components/funds/fund-drawdown-card";
import { FundExpenseChart } from "@/components/funds/fund-expense-chart";
import { FundTaxInfo } from "@/components/funds/fund-tax-info";
import { FundPracticalInfo } from "@/components/funds/fund-practical-info";
import { FundUpgradeCta } from "@/components/funds/fund-upgrade-cta";
import { FundSipChart } from "@/components/funds/fund-sip-chart";
import { FundRollingChart } from "@/components/funds/fund-rolling-chart";
import { FundYearlyChart } from "@/components/funds/fund-yearly-chart";
// Data-bound inline displays (Session A Task 2)
import { FundReturnsTable } from "@/components/funds/fund-returns-table";
import { FundRiskMetrics } from "@/components/funds/fund-risk-metrics";
import { FundCostImpact } from "@/components/funds/fund-cost-impact";
import { FundPortfolioMetrics } from "@/components/funds/fund-portfolio-metrics";
import {
  FileText,
  DollarSign,
  TrendingUp,
  Landmark,
  User,
  AlertTriangle,
  Target,
  Newspaper,
  Receipt,
  Info,
  Lock,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

const SECTION_ICONS: Record<string, LucideIcon> = {
  fundSnapshot: FileText,
  costBreakdown: DollarSign,
  performanceAnalysis: TrendingUp,
  portfolioHoldings: Landmark,
  fundManager: User,
  riskAnalysis: AlertTriangle,
  wtmAssessment: Target,
  newsChanges: Newspaper,
  taxInfo: Receipt,
};

// ─── Sticky Table of Contents (desktop only) ──────────────────────────────
// Mirrors the stock report TOC pattern: fixed left sidebar, intersection
// observer drives the active highlight, PRO-locked sections show a lock icon.
// The "Practical Info" entry is pinned to the end and tracked as index 9
// (beyond FUND_SECTION_META.length which is 9).
function FundTableOfContents({
  activeSection,
  onClickSection,
  isFreeUser,
  hasIndustry,
}: {
  activeSection: number;
  onClickSection: (index: number) => void;
  isFreeUser: boolean;
  hasIndustry?: boolean;
}) {
  return (
    <nav className="hidden lg:block fixed top-[72px] w-[200px] py-6">
      <div className="text-dim text-[10px] uppercase tracking-widest mb-3 pl-3">
        Contents
      </div>
      <div className="space-y-0.5">
        {FUND_SECTION_META.map((meta, i) => {
          const Icon = SECTION_ICONS[meta.key] || FileText;
          const isLocked = isFreeUser && !meta.isFree;
          return (
            <Fragment key={meta.key}>
              <button
                onClick={() => onClickSection(i)}
                className={`w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none ${
                  activeSection === i
                    ? "text-blue bg-blue/10 font-semibold"
                    : "text-dim hover:text-muted-foreground"
                }`}
              >
                <Icon className="w-3 h-3 shrink-0" />
                <span className="truncate flex-1">{meta.title}</span>
                {isLocked && <Lock className="w-3 h-3 shrink-0 text-dim" />}
              </button>
              {meta.key === "fundSnapshot" && hasIndustry && (
                <button
                  onClick={() => {
                    document
                      .getElementById("fund-industry-landscape")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none text-dim hover:text-muted-foreground"
                >
                  <TrendingUp className="w-3 h-3 shrink-0" />
                  <span className="truncate flex-1">Industry Landscape</span>
                </button>
              )}
            </Fragment>
          );
        })}
        <button
          onClick={() => onClickSection(FUND_SECTION_META.length)}
          className={`w-full flex items-center gap-2 text-left px-3 py-1.5 rounded-md text-[12px] transition-all cursor-pointer bg-transparent border-none ${
            activeSection === FUND_SECTION_META.length
              ? "text-blue bg-blue/10 font-semibold"
              : "text-dim hover:text-muted-foreground"
          }`}
        >
          <Info className="w-3 h-3 shrink-0" />
          <span className="truncate flex-1">Practical Info</span>
        </button>
      </div>
    </nav>
  );
}

type FundReportData = {
  id: string;
  slug: string;
  status: string;
  dataMonth: string | null;
  wtmScore: number | null;
  scoreBreakdown: Record<string, { label: string; score: number; reasoning: string }> | null;
  content: FundReportContent;
  dataSnapshot: PreCalculatedFundData | null;
  summaryText: string | null;
  agreeCount: number;
  disagreeCount: number;
  createdAt: string;
  fund: {
    schemeCode: string;
    schemeName: string;
    amc: string | null;
    category: string | null;
    fundType: string | null;
    schemeType: string | null;
    planType: string | null;
    nav: number | null;
    navDate: string | null;
    aum: number | null;
    expenseRatio: number | null;
    morningstarRating: number | null;
    currency: string;
  };
};

function ReportSkeleton() {
  return (
    <div className="min-h-screen bg-navy">
      <Navbar />
      <div className="max-w-[800px] mx-auto px-6 py-6">
        <Skeleton className="h-4 w-24 mb-4" />
        <Skeleton className="h-64 rounded-2xl mb-5" />
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function FundReportPage() {
  const params = useParams();
  const slug = params.slug as string;
  const { data: session } = useSession();

  const [report, setReport] = useState<FundReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"financial" | "simple">("simple");
  const [showPaywall, setShowPaywall] = useState(false);
  const [userPlan, setUserPlan] = useState("FREE");

  // Vote state
  const [userVote, setUserVote] = useState<string | null>(null);
  const [agreeCount, setAgreeCount] = useState(0);
  const [disagreeCount, setDisagreeCount] = useState(0);

  // Glossary terms for inline tooltips (fetched once, shared across sections)
  const [glossaryTerms, setGlossaryTerms] = useState<GlossaryEntry[]>([]);

  // Share-copied toast state
  const [shareCopied, setShareCopied] = useState(false);

  // TOC active-section tracking (index into FUND_SECTION_META; last index
  // is reserved for the Practical Info block).
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollToSection = useCallback((index: number) => {
    const el = sectionRefs.current[index];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  // Fetch report
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/fund-reports/${slug}`);
        const json = await res.json();
        if (json.success) {
          setReport(json.data);
          setAgreeCount(json.data.agreeCount);
          setDisagreeCount(json.data.disagreeCount);
        } else {
          setError(json.error || "Report not found");
        }
      } catch {
        setError("Failed to load report");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Fetch glossary terms once (served from Redis, 7-day cache)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/api/glossary");
        const json = await res.json();
        if (json.success && Array.isArray(json.data)) {
          setGlossaryTerms(
            json.data.map((t: GlossaryEntry) => ({
              id: t.id,
              term: t.term,
              slug: t.slug,
              definitionSimple: t.definitionSimple,
            }))
          );
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Fetch user plan
  useEffect(() => {
    if (!session?.user) return;
    (async () => {
      try {
        const res = await fetch("/api/user/plan");
        const json = await res.json();
        if (json.success) setUserPlan(json.plan);
      } catch { /* ignore */ }
    })();
  }, [session]);

  // Fetch vote state
  useEffect(() => {
    if (!report || !session?.user) return;
    (async () => {
      try {
        const res = await fetch(`/api/fund-reports/${report.id}/vote`);
        const json = await res.json();
        if (json.success) {
          setUserVote(json.data.userVote);
        }
      } catch { /* ignore */ }
    })();
  }, [report, session]);

  const handleVote = useCallback(
    async (vote: "AGREE" | "DISAGREE") => {
      if (!session?.user || !report) return;
      try {
        const res = await fetch(`/api/fund-reports/${report.id}/vote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ vote }),
        });
        const json = await res.json();
        if (json.success) {
          setUserVote(json.data.userVote);
          setAgreeCount(json.data.agreeCount);
          setDisagreeCount(json.data.disagreeCount);
        }
      } catch { /* ignore */ }
    },
    [session, report]
  );

  // IntersectionObserver — highlights the TOC entry for whatever section
  // is currently most visible. Matches the stock report pattern.
  useEffect(() => {
    if (!report) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = sectionRefs.current.indexOf(entry.target as HTMLDivElement);
            if (idx !== -1) setActiveSection(idx);
          }
        }
      },
      { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
    );
    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, [report]);

  const canAccess = useCallback(
    (isFree: boolean) => {
      if (isFree) return true;
      return userPlan === "PRO" || userPlan === "PREMIUM";
    },
    [userPlan]
  );

  if (loading) return <ReportSkeleton />;

  if (error || !report) {
    return (
      <div className="min-h-screen bg-navy">
        <Navbar />
        <div className="max-w-[800px] mx-auto px-6 py-20 text-center">
          <h1 className="text-foreground text-xl font-serif mb-2">Report Not Found</h1>
          <p className="text-muted-foreground text-sm mb-4">{error || "This fund report does not exist."}</p>
          <Link
            href="/funds"
            className="text-blue text-sm hover:underline no-underline"
          >
            Browse Fund Reports
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const content = report.content;
  const fund = report.fund;
  const snapshot = report.dataSnapshot;
  const fundIndustryAnalysis =
    (content as { industryAnalysis?: EmbeddedFundIndustryAnalysis }).industryAnalysis ?? null;
  const isElss = (fund.fundType ?? "").toLowerCase().includes("elss");
  const lockInYears = isElss ? 3 : 0;
  const isFreeUser = userPlan !== "PRO" && userPlan !== "PREMIUM";

  return (
    <div className="min-h-screen bg-navy">
      <Navbar />
      <FundTableOfContents
        activeSection={activeSection}
        onClickSection={scrollToSection}
        isFreeUser={isFreeUser}
        hasIndustry={!!fundIndustryAnalysis && fundIndustryAnalysis.sectorsAnalyzed.length > 0}
      />
      <div className="max-w-[800px] mx-auto px-6 py-6 lg:ml-[220px] lg:max-w-[780px]">
        {/* Back nav */}
        <div className="flex items-center gap-3 mb-4 text-sm">
          <Link href="/funds" className="text-muted-foreground hover:text-foreground no-underline transition">
            Funds
          </Link>
          <span className="text-dim">/</span>
          <span className="text-foreground">{fund.schemeName}</span>
        </div>

        {/* Summary card */}
        <FundSummaryCard
          schemeName={fund.schemeName}
          amc={fund.amc ?? ""}
          category={fund.category ?? ""}
          nav={fund.nav ?? 0}
          navDate={fund.navDate ?? ""}
          aum={fund.aum}
          expenseRatio={fund.expenseRatio ?? 0}
          morningstarRating={fund.morningstarRating ?? 0}
          wtmScore={report.wtmScore ?? undefined}
          returns={{
            y1: snapshot?.returns.oneYear != null ? snapshot.returns.oneYear * 100 : null,
            y3: snapshot?.returns.threeYearCagr != null ? snapshot.returns.threeYearCagr * 100 : null,
            y5: snapshot?.returns.fiveYearCagr != null ? snapshot.returns.fiveYearCagr * 100 : null,
          }}
          planType={fund.planType ?? "Direct"}
          currency={fund.currency}
          generatedAt={report.createdAt}
          dataMonth={report.dataMonth ?? ""}
          onShare={() => {
            const url = `${window.location.origin}/funds/${report.slug}`;
            navigator.clipboard.writeText(url);
            setShareCopied(true);
            setTimeout(() => setShareCopied(false), 2000);
          }}
          onDownloadPdf={() => {
            if (userPlan !== "PRO" && userPlan !== "PREMIUM") {
              setShowPaywall(true);
              return;
            }
            window.open(`/api/fund-reports/${report.slug}/pdf`, "_blank");
          }}
        />

        {/* Dual tab toggle — sticky so it stays visible as the user scrolls
            through sections. `top-16` accounts for the navbar height. */}
        <div className="sticky top-16 z-30 -mx-6 px-6 mt-6 mb-4 py-3 bg-navy/85 backdrop-blur-md border-b border-surface-light/10 flex items-center justify-between flex-wrap gap-3">
          <DualTabToggle activeTab={activeTab} onTabChange={setActiveTab} />
          {/* Vote buttons */}
          {session?.user && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleVote("AGREE")}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
                  userVote === "AGREE"
                    ? "bg-cgreen/20 text-cgreen border-cgreen/40"
                    : "bg-surface text-muted-foreground border-surface-light/30 hover:border-cgreen/40"
                }`}
              >
                &#128077; Agree {agreeCount > 0 && <span className="font-tabular">{agreeCount}</span>}
              </button>
              <button
                onClick={() => handleVote("DISAGREE")}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer border ${
                  userVote === "DISAGREE"
                    ? "bg-cred/20 text-cred border-cred/40"
                    : "bg-surface text-muted-foreground border-surface-light/30 hover:border-cred/40"
                }`}
              >
                &#128078; Disagree {disagreeCount > 0 && <span className="font-tabular">{disagreeCount}</span>}
              </button>
            </div>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-2">
          {FUND_SECTION_META.map((s, i) => {
            const sectionKey = s.key as keyof FundReportContent;
            const sectionData = content[sectionKey];
            if (!sectionData) return <Fragment key={s.key} />;

            const isFree = s.isFree;
            const locked = !canAccess(isFree);

            const financialText =
              typeof (sectionData as Record<string, unknown>).financial === "string"
                ? ((sectionData as Record<string, unknown>).financial as string)
                : "";
            const simpleText =
              typeof (sectionData as Record<string, unknown>).simple === "string"
                ? ((sectionData as Record<string, unknown>).simple as string)
                : "";

            // Render the text section
            const sectionNode = (
              <FundSection
                title={s.title}
                icon={SECTION_ICONS[s.key] || FileText}
                financialContent={financialText}
                simpleContent={simpleText}
                activeTab={activeTab}
                defaultExpanded={i < 3}
                isLocked={locked}
                glossaryTerms={glossaryTerms}
              />
            );

            // Build any chart extras that accompany this section
            let extras: React.ReactNode = null;

            if (!locked && snapshot) {
              if (s.key === "costBreakdown") {
                extras = (
                  <div className="mt-2 space-y-3">
                    <FundExpenseChart
                      expensePerLakh={snapshot.cost.expensePerLakhAnnual}
                      categoryAvgExpensePerLakh={snapshot.cost.categoryAvgExpensePerLakh}
                      regularPlanExpensePerLakh={snapshot.cost.regularPlanExpensePerLakh}
                    />
                    <FundCostImpact
                      expensePerLakhAnnual={snapshot.cost.expensePerLakhAnnual}
                      expenseSavingVsCategory={snapshot.cost.expenseSavingVsCategory}
                      directVsRegularTenYearImpact={snapshot.cost.directVsRegularTenYearImpact}
                    />
                  </div>
                );
              } else if (s.key === "performanceAnalysis") {
                extras = (
                  <div className="mt-2 space-y-3">
                    <FundReturnsChart
                      fundName={fund.schemeName}
                      returns={{
                        oneMonth: snapshot.returns.oneMonth,
                        threeMonth: snapshot.returns.threeMonth,
                        sixMonth: snapshot.returns.sixMonth,
                        oneYear: snapshot.returns.oneYear,
                        threeYearCagr: snapshot.returns.threeYearCagr,
                        fiveYearCagr: snapshot.returns.fiveYearCagr,
                      }}
                    />
                    <FundYearlyChart yearlyReturns={snapshot.returns.yearlyReturns} />
                    <FundSipChart
                      threeYearMonthly={snapshot.returns.sipTenKThreeYearMonthly}
                      fiveYearMonthly={snapshot.returns.sipTenKFiveYearMonthly}
                      threeYearXirrPct={snapshot.returns.sipTenKThreeYearXirr}
                      fiveYearXirrPct={snapshot.returns.sipTenKFiveYearXirr}
                    />
                    <FundRollingChart
                      distribution={snapshot.returns.rollingThreeYearDistribution}
                      bestPct={snapshot.returns.rollingThreeYearBest}
                      worstPct={snapshot.returns.rollingThreeYearWorst}
                      medianPct={snapshot.returns.rollingThreeYearMedian}
                    />
                    <FundReturnsTable
                      oneMonth={snapshot.returns.oneMonth}
                      threeMonth={snapshot.returns.threeMonth}
                      sixMonth={snapshot.returns.sixMonth}
                      oneYear={snapshot.returns.oneYear}
                      threeYearCagr={snapshot.returns.threeYearCagr}
                      fiveYearCagr={snapshot.returns.fiveYearCagr}
                      sinceInceptionCagr={snapshot.returns.sinceInceptionCagr}
                      lakhOneYearValue={snapshot.returns.lakhOneYearValue}
                      lakhThreeYearValue={snapshot.returns.lakhThreeYearValue}
                      lakhFiveYearValue={snapshot.returns.lakhFiveYearValue}
                    />
                  </div>
                );
              } else if (s.key === "portfolioHoldings") {
                const holdings = sectionData as NonNullable<
                  FundReportContent["portfolioHoldings"]
                >;
                extras = (
                  <div className="mt-2 space-y-3">
                    <FundSectorChart
                      sectorAllocation={
                        snapshot.holdings?.sectorAllocation ??
                        holdings.sectorAllocation ??
                        null
                      }
                      totalStocks={snapshot.holdings?.totalStocks ?? null}
                    />
                    {holdings.topHoldings && holdings.topHoldings.length > 0 && (
                      <div className="bg-surface border border-surface-light/30 rounded-xl p-5">
                        <FundHoldingsChart
                          sectors={holdings.sectorAllocation ?? []}
                          topHoldings={holdings.topHoldings}
                        />
                      </div>
                    )}
                    <FundPortfolioMetrics
                      top5ConcentrationPct={snapshot.holdings?.top5ConcentrationPct ?? null}
                      top10ConcentrationPct={snapshot.holdings?.top10ConcentrationPct ?? null}
                      equityPct={snapshot.holdings?.equityPct ?? null}
                      debtPct={snapshot.holdings?.debtPct ?? null}
                      otherPct={snapshot.holdings?.otherPct ?? null}
                    />
                  </div>
                );
              } else if (s.key === "riskAnalysis") {
                extras = (
                  <div className="mt-2 space-y-3">
                    <FundDrawdownCard
                      maxDrawdownPct={snapshot.risk.maxDrawdownPct}
                      maxDrawdownLakhValue={snapshot.risk.maxDrawdownLakhValue}
                      peakDate={snapshot.risk.maxDrawdownPeakDate}
                      troughDate={snapshot.risk.maxDrawdownTroughDate}
                      recoveryMonths={snapshot.risk.maxDrawdownRecoveryMonths}
                    />
                    <FundRiskMetrics
                      sharpeRatio={snapshot.risk.sharpeRatio}
                      sortinoRatio={snapshot.risk.sortinoRatio}
                      beta={snapshot.risk.beta}
                      standardDeviation={snapshot.risk.standardDeviation}
                      volatilityScale={snapshot.risk.volatilityScale1to10}
                      rSquared={snapshot.risk.rSquared}
                    />
                  </div>
                );
              } else if (s.key === "wtmAssessment") {
                const assessment = sectionData as NonNullable<
                  FundReportContent["wtmAssessment"]
                >;
                const subScoreValues = assessment.subScores
                  ? {
                      costEfficiency: assessment.subScores.costEfficiency?.score ?? null,
                      performanceConsistency:
                        assessment.subScores.performanceConsistency?.score ?? null,
                      riskManagement: assessment.subScores.riskManagement?.score ?? null,
                      fundManagerQuality:
                        assessment.subScores.fundManagerQuality?.score ?? null,
                      portfolioQuality:
                        assessment.subScores.portfolioQuality?.score ?? null,
                      transparency: assessment.subScores.transparency?.score ?? null,
                    }
                  : null;
                extras = (
                  <div className="mt-2 space-y-3">
                    <FundScoreRadar
                      overallScore={assessment.overallScore ?? report.wtmScore ?? null}
                      subScores={subScoreValues}
                    />
                    {assessment.subScores && (
                      <div className="bg-surface border border-surface-light/30 rounded-xl p-5">
                        <FundScoreBreakdown
                          overall={assessment.overallScore ?? report.wtmScore ?? 0}
                          breakdown={
                            assessment.subScores as unknown as Record<
                              string,
                              { label: string; score: number; reasoning: string }
                            >
                          }
                        />
                      </div>
                    )}
                  </div>
                );
              } else if (s.key === "taxInfo") {
                extras = (
                  <div className="mt-2">
                    <FundTaxInfo
                      fundType={fund.fundType ?? ""}
                      isElss={isElss}
                    />
                  </div>
                );
              }
            }

            return (
              <Fragment key={s.key}>
                <div
                  ref={(el) => {
                    sectionRefs.current[i] = el;
                  }}
                  data-section-key={s.key}
                >
                  {sectionNode}
                  {extras}
                </div>
                {s.key === "fundSnapshot" && fundIndustryAnalysis && fundIndustryAnalysis.sectorsAnalyzed.length > 0 && (
                  <FundIndustrySection data={fundIndustryAnalysis} />
                )}
              </Fragment>
            );
          })}
        </div>

        {/* Upgrade CTA for free users (in place of locked PRO sections) */}
        {isFreeUser && (
          <FundUpgradeCta fundName={fund.schemeName} />
        )}

        {/* Practical info grid — visible to all tiers. Tracked as the final
            TOC entry (index = FUND_SECTION_META.length). */}
        {snapshot && (
          <div
            ref={(el) => {
              sectionRefs.current[FUND_SECTION_META.length] = el;
            }}
          >
            <FundPracticalInfo
              minSip={snapshot.cost.minSip}
              minLumpsum={snapshot.cost.minLumpsum}
              exitLoad={snapshot.cost.exitLoad}
              lockInPeriod={lockInYears}
              fundAgeText={snapshot.fundAgeText}
              fundType={fund.fundType ?? ""}
              sidUrl={null}
            />
          </div>
        )}

        {/* Summary */}
        {report.summaryText && (
          <div className="bg-surface border border-surface-light/30 rounded-xl p-5 mt-5">
            <h3 className="text-foreground text-sm font-semibold mb-2">Summary</h3>
            <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
              {stripMarkdown(report.summaryText)}
            </p>
          </div>
        )}

        {/* Disclaimer. RM-W1-11: it names the same NAV date the summary card prints, so the page
            makes one statement about how current the data is rather than two. */}
        <FundDisclaimer navDate={fund.navDate} />
      </div>

      <FundPaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} />

      {/* Share-copied toast */}
      {shareCopied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-cgreen text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-lg animate-in fade-in slide-in-from-bottom-2">
          ✓ Link copied to clipboard
        </div>
      )}
      <Footer />
    </div>
  );
}
